import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, CheckCircle, Shield, Award, Users, Clock } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import SEOHead from '../components/common/SEOHead';

const ABOUT_IMAGE = 'https://customer-assets.emergentagent.com/job_b8d07c5d-fcca-4dc8-ad54-fc1da9664427/artifacts/atrvqanj_IMAGENS%20PARA%20SITE1.png';
const WORK_IMAGE = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/0x5tp7x0_IMAGENS%20PARA%20SITE6.png';
const WORK_IMAGE_2 = 'https://customer-assets.emergentagent.com/job_fort-build-uk/artifacts/lm4nc845_IMAGENS%20PARA%20SITE5.png';

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: 'easeOut' }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.15 }
  }
};

const staggerItem = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 }
};

const AboutPage = () => {
  const { t } = useLanguage();

  const safetyItems = [
    t('about.safety.items.regulations'),
    t('about.safety.items.training'),
    t('about.safety.items.equipment'),
    t('about.safety.items.assessments'),
    t('about.safety.items.insurance'),
  ];

  const stats = [
    { icon: Clock, value: '5+', label: 'Years Experience' },
    { icon: Users, value: '100+', label: 'Projects Completed' },
    { icon: Award, value: '100%', label: 'Client Satisfaction' },
    { icon: Shield, value: '100%', label: 'Safety Record' },
  ];

  return (
    <>
      <SEOHead 
        title={t('meta.about.title')}
        description={t('meta.about.description')}
      />

      {/* Hero Section */}
      <section 
        className="relative pt-32 pb-20 bg-navy-deep"
        data-testid="about-hero"
      >
        <div className="container-main">
          <div className="max-w-3xl">
            <motion.span 
              className="text-gold-main text-sm font-semibold uppercase tracking-widest block mb-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {t('about.hero.label')}
            </motion.span>
            <motion.h1 
              className="font-alatsi text-5xl md:text-6xl text-white leading-tight mb-6"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              data-testid="about-title"
            >
              {t('about.hero.title')}
            </motion.h1>
            <motion.p 
              className="text-xl text-gray-300 leading-relaxed"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              {t('about.hero.subtitle')}
            </motion.p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white border-b border-gray-100" data-testid="stats-section">
        <div className="container-main">
          <motion.div 
            className="grid grid-cols-2 md:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="show"
            viewport={{ once: true }}
          >
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                variants={staggerItem}
                className="text-center"
              >
                <stat.icon className="w-8 h-8 text-gold-main mx-auto mb-3" strokeWidth={1.5} />
                <div className="font-alatsi text-4xl text-navy-deep mb-1">{stat.value}</div>
                <div className="text-sm text-slate-500 uppercase tracking-wide">{stat.label}</div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Our Story Section */}
      <section className="section-padding bg-white" data-testid="story-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeUp}>
              <h2 className="font-alatsi text-4xl md:text-5xl text-navy-deep mb-8">
                {t('about.story.title')}
              </h2>
              <div className="space-y-6 text-lg text-slate-600 leading-relaxed">
                <p>{t('about.story.p1')}</p>
                <p>{t('about.story.p2')}</p>
                <p>{t('about.story.p3')}</p>
                <p>{t('about.story.p4')}</p>
                <p className="font-semibold text-navy-deep">{t('about.story.p5')}</p>
              </div>
            </motion.div>

            <motion.div 
              className="relative"
              {...fadeUp}
            >
              <img 
                src={ABOUT_IMAGE}
                alt="EuroFortaleza team leader on construction site"
                className="w-full h-auto"
              />
              <div className="absolute -bottom-6 -left-6 bg-gold-main p-6 hidden lg:block">
                <p className="font-alatsi text-2xl text-navy-deep">5+ Years</p>
                <p className="text-navy-deep/80 text-sm">of Excellence</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Work Image Section */}
      <section className="py-0 grid grid-cols-1 md:grid-cols-2">
        <img 
          src={WORK_IMAGE}
          alt="Welding construction work"
          className="w-full h-64 md:h-80 object-cover"
        />
        <img 
          src={WORK_IMAGE_2}
          alt="Metal structure installation"
          className="w-full h-64 md:h-80 object-cover"
        />
      </section>

      {/* Safety & Compliance Section */}
      <section className="section-padding bg-slate-50" data-testid="safety-section">
        <div className="container-main">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <motion.div 
              className="order-2 lg:order-1 bg-navy-deep p-10 md:p-12"
              {...fadeUp}
            >
              <div className="space-y-4">
                {safetyItems.map((item, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: index * 0.1 }}
                    className="flex items-start space-x-4"
                  >
                    <CheckCircle className="w-6 h-6 text-gold-main flex-shrink-0 mt-0.5" />
                    <p className="text-white">{item}</p>
                  </motion.div>
                ))}
              </div>
            </motion.div>

            <motion.div 
              className="order-1 lg:order-2"
              {...fadeUp}
            >
              <span className="label-text block mb-4">{t('about.safety.label')}</span>
              <h2 className="font-alatsi text-3xl md:text-4xl text-navy-deep mb-6">
                {t('about.safety.title')}
              </h2>
              <p className="text-lg text-slate-600 leading-relaxed mb-8">
                {t('about.safety.description')}
              </p>
              <Link
                to="/services"
                className="inline-flex items-center space-x-3 bg-navy-deep text-white hover:bg-gold-main transition-colors duration-300 px-8 py-4 uppercase tracking-widest text-sm font-semibold"
                data-testid="about-services-link"
              >
                <span>{t('services.viewAll')}</span>
                <ArrowRight className="w-5 h-5" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gold-main" data-testid="about-cta">
        <div className="container-main text-center">
          <motion.h2 
            className="font-alatsi text-3xl md:text-4xl text-navy-deep mb-8"
            {...fadeUp}
          >
            {t('hero.title')}
          </motion.h2>
          <motion.div {...fadeUp}>
            <Link
              to="/get-in-touch"
              className="inline-flex items-center space-x-3 bg-navy-deep text-white hover:bg-navy-light transition-colors duration-300 px-10 py-5 uppercase tracking-widest text-sm font-semibold"
              data-testid="about-cta-btn"
            >
              <span>{t('hero.cta')}</span>
              <ArrowRight className="w-5 h-5" />
            </Link>
          </motion.div>
        </div>
      </section>
    </>
  );
};

export default AboutPage;
